import { Kurv, KurvLight } from '../../../models';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { renderReadonlyCheckboxCell } from '../../../app/helpers/columnHelper';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { EditPanelDataGrid } from '../../../app/common/components/EditPanelDataGrid/EditPanelDataGrid';
import { RegisterCodeEnum } from '../../../models/Constants/ValgRegister';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { MultipleActionBtnComponent } from '../../../app/common/components/EditPanelDataGrid/CustomComponents';
import Controls from '../../../app/common/controls/Controls';
import { kurvQueryApiService } from '../../../api/rtk-queries/extensions/kurverQueryApiService';
import { GetAllParentKurver } from '../kurvHelper';
import makeStyles from '@mui/styles/makeStyles';
import { AndFilter, FieldFilter, OrFilter } from '../../../app/utils/filter';
import { ComparisonOperators } from '../../../models/Enum/FilterProperties';
import { FetchArg } from '../../../api/rtk-queries/queryApiCreator';

export interface UnderKurverListProps {
  underKurver: KurvLight[];
  onChangeUnderKurver: (val: KurvLight[]) => void;
  redigereKurv: KurvLight;
  testId?: string;
  canEdit: boolean;
  disabled?: boolean;
}
export const dataGridSelectStyle = makeStyles((theme) => ({
  datagridDisabledRow: {
    backgroundColor: theme.palette.grey[300]
  },
}));



export function UnderKurverList(props: UnderKurverListProps) {
  const { underKurver, onChangeUnderKurver, redigereKurv, testId = '', canEdit, disabled = false } = props;
  const { t } = useTranslation();
  const [openUnderKurverAdding, setOpenUnderKurverAdding] = useState(false);
  const [selectionModel, setSelectionModel] = useState<KurvLight[]>([]);
  const allKurver = kurvQueryApiService.api.useSearchKurvQuery({ disablePagination: true, q: '' }).data?.value;
  const [isNotSelectableKurver, setIsNotSelectableKurver] = useState<Kurv[]>([]);
  const { datagridDisabledRow } = dataGridSelectStyle();

  const getNotSelectableKurver = useCallback(() =>
    GetAllParentKurver(
      allKurver ?? [],
      allKurver?.find(i => i.id === redigereKurv.id) as Kurv,
      []),
    [allKurver, redigereKurv]);

  useEffect(() => {
    setIsNotSelectableKurver(getNotSelectableKurver());
  }, [getNotSelectableKurver]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: t('Kode'),
      type: 'string',
      sortable: true,
      valueGetter: (p) => p.row['id'],
    },
    {
      field: 'tittel',
      headerName: t('Navn'),
      type: 'string',
      sortable: true,
    },
    {
      field: 'beskrivelse',
      headerName: t('Beskrivelse'),
      type: 'string',
      sortable: true,
    },
    {
      field: 'sperret',
      headerName: t('Stengt for bruk'),
      cellClassName: 'checkbox--cell',
      renderCell: renderReadonlyCheckboxCell
    },
    {
      field: 'unique',
      headerName: t('Id'),
      hide: true
    },
  ];



  function handleMultipleDelete() {
    onChangeUnderKurver(underKurver.filter(l => !selectionModel.some(sel => sel.id === l.id)));
    setSelectionModel([]);
  }

  const isSelectableRow = useCallback(
    (k: GridRowParams) => !isNotSelectableKurver.some(i => i.id === k.id) || underKurver.some(i => i.id === k.id),
    [isNotSelectableKurver, underKurver]);

  const getKurverOptions = useCallback(async (q: string) => {
    const filterNotIncludeRedigereKurv = { parameters: FieldFilter.create('Id', ComparisonOperators.NotEqual, redigereKurv.id) };

    if (redigereKurv?.id) {
      if (!q.length) {
        let result = (await kurvQueryApiService.advancedOptionsPlainSearch({ filterNotIncludeRedigereKurv } as FetchArg<Kurv>));
        return result;
      }
      const filter = {
        parameters: AndFilter.create(
          filterNotIncludeRedigereKurv.parameters,
          OrFilter.combine([
            FieldFilter.create('Id', ComparisonOperators.Contains, q),
            FieldFilter.create('Tittel', ComparisonOperators.Contains, q)]
          ))
      };
      return await kurvQueryApiService.advancedOptionsPlainSearch({ filter } as FetchArg<Kurv>);
    }
    return [];
  }, [redigereKurv.id]);

  return (
    <>
      <EditPanelDataGrid
        disabled={disabled}
        gridId={`${RegisterCodeEnum.Kurv}_${RegisterCodeEnum.Kurv}`}
        rows={underKurver ?? []}
        columns={columns}
        enableContextMeny={false}
        testId={testId}
        setSelectedRows={setSelectionModel}

        checkboxSelection={true}
        componentsProps={{
          toolbar: {
            addButton: {
              title: t('Legg til'),
              onClick: () => setOpenUnderKurverAdding(true),
              disabled: disabled
            },
            showRefresh: false,
            buttons: (
              <MultipleActionBtnComponent
                disabled={!canEdit || selectionModel.length === 0}
                handleActionBtn={handleMultipleDelete}
                clearSelection={() => setSelectionModel([])}
                title={t('Slett')}
                icon={<DeleteSweepIcon color={selectionModel.length === 0 ? 'disabled' : 'primary'} />}
              />
            ),
          },
        }}
      />
      {openUnderKurverAdding && (
        <Controls.ModalMultiSelect
          gridId={'underkurver'}
          name='underkurver'
          title={t('Kurver')}
          isRowSelectable={(param) => isSelectableRow(param)}
          dialogTitle={t('Underkurver')}
          testId={testId}
          getRowClassName={(params: any) => !isSelectableRow(params) ? datagridDisabledRow : ''
          }
          isOptionEqualToValue={(kurv, underKurv) => kurv.id === underKurv.id}
          columns={columns}
          isOpen={openUnderKurverAdding}
          getOptions={getKurverOptions}
          close={() => setOpenUnderKurverAdding(false)}
          value={underKurver as Kurv[] ?? []}
          onChange={onChangeUnderKurver}
        />
      )}
    </>
  );
}
